import type {
  AppNotification,
  ApiKey,
  AppSettings,
  Blockout,
  Plan,
  PlanItem,
  SchedulingAssignment,
  SchedulingStatus,
  TeamMembership,
} from '@/types'
import { nowISO } from '@/lib/utils'
import { toISODate } from '@/lib/format'
import { CURRENT_PERSON_ID, buildMemberships, buildPositions, buildServiceTypes } from '@/db/seed.org'

/** The Sunday on/after `from`, then every following week. */
export function upcomingSundays(count: number, from = new Date()): string[] {
  const start = new Date(from.getFullYear(), from.getMonth(), from.getDate())
  const offsetToSunday = (7 - start.getDay()) % 7
  start.setDate(start.getDate() + offsetToSunday)
  return Array.from({ length: count }, (_, i) => {
    const date = new Date(start)
    date.setDate(start.getDate() + i * 7)
    return toISODate(date)
  })
}

function wednesdayAfter(sunday: string): string {
  const [y, m, d] = sunday.split('-').map(Number)
  const date = new Date(y, m - 1, d + 3)
  return toISODate(date)
}

const SERIES = [
  { title: 'Rooted', color: '#6366f1' },
  { title: 'Rooted', color: '#6366f1' },
  { title: 'Rooted', color: '#6366f1' },
  { title: 'Everyday Faith', color: '#0ea5e9' },
  { title: 'Everyday Faith', color: '#0ea5e9' },
  { title: 'Everyday Faith', color: '#0ea5e9' },
]

const SERMON_TITLES = [
  'Deep Roots, Steady Fruit',
  'When the Wind Comes',
  'Growing in the Dark',
  'Faith at the Kitchen Table',
  'Faith on Monday Morning',
  'Faith That Finishes',
]

const SONG_ROTATION = [
  ['song_holy_holy_holy', 'song_every_morning_new', 'song_amazing_grace', 'song_doxology'],
  ['song_every_morning_new', 'song_come_thou_fount', 'song_river_of_mercy', 'song_doxology'],
  ['song_blessed_assurance', 'song_amazing_grace', 'song_be_thou_my_vision', 'song_doxology'],
  ['song_crown_him', 'song_every_morning_new', 'song_it_is_well', 'song_doxology'],
  ['song_holy_holy_holy', 'song_river_of_mercy', 'song_come_thou_fount', 'song_doxology'],
  ['song_every_morning_new', 'song_be_thou_my_vision', 'song_amazing_grace', 'song_doxology'],
]

const DEFAULT_ARRANGEMENT: Record<string, string> = {
  song_amazing_grace: 'arr_amazing_grace_default',
  song_come_thou_fount: 'arr_come_thou_fount_default',
  song_be_thou_my_vision: 'arr_be_thou_my_vision_default',
  song_it_is_well: 'arr_it_is_well_default',
  song_holy_holy_holy: 'arr_holy_holy_holy_default',
  song_blessed_assurance: 'arr_blessed_assurance_default',
  song_doxology: 'arr_doxology_default',
  song_crown_him: 'arr_crown_him_default',
  song_every_morning_new: 'arr_every_morning_new_default',
  song_river_of_mercy: 'arr_river_of_mercy_default',
}

const SONG_TITLES: Record<string, string> = {
  song_amazing_grace: 'Amazing Grace',
  song_come_thou_fount: 'Come Thou Fount of Every Blessing',
  song_be_thou_my_vision: 'Be Thou My Vision',
  song_it_is_well: 'It Is Well With My Soul',
  song_holy_holy_holy: 'Holy, Holy, Holy',
  song_blessed_assurance: 'Blessed Assurance',
  song_doxology: 'Doxology',
  song_crown_him: 'Crown Him With Many Crowns',
  song_every_morning_new: 'Every Morning New',
  song_river_of_mercy: 'River of Mercy',
}

export interface PlanBundle {
  plans: Plan[]
  planItems: PlanItem[]
  assignments: SchedulingAssignment[]
}

/** Builds the order of service for one plan. */
function buildItems(planId: string, weekIndex: number, now: string): PlanItem[] {
  const songs = SONG_ROTATION[weekIndex % SONG_ROTATION.length]
  const sermonTitle = SERMON_TITLES[weekIndex % SERMON_TITLES.length]

  const raw: Array<Partial<PlanItem> & { title: string; type: PlanItem['type']; durationSeconds: number }> = [
    {
      type: 'header',
      title: 'Pre-Service',
      durationSeconds: 0,
      description: 'Doors open 20 minutes before start.',
    },
    {
      type: 'media',
      title: 'Countdown & Announcements Loop',
      durationSeconds: 600,
      techNotes: {
        audio: 'Walk-in playlist at -18 LUFS. Fade under at 0:30.',
        video: 'Countdown template on all screens + stream.',
        lighting: 'House at 70%, stage wash warm.',
      },
    },
    { type: 'header', title: 'Gathering', durationSeconds: 0 },
    {
      type: 'custom',
      title: 'Welcome & Call to Worship',
      durationSeconds: 180,
      responsiblePersonId: 'person_ian',
      description: 'Short welcome, then straight into the first song.',
      techNotes: { audio: 'Handheld 1 live, music beds off.', lighting: 'Stage to 100%.' },
    },
    {
      type: 'song',
      title: SONG_TITLES[songs[0]],
      durationSeconds: 300,
      songId: songs[0],
      arrangementId: DEFAULT_ARRANGEMENT[songs[0]],
      techNotes: { audio: 'Full band. Watch BV levels in the last chorus.', video: 'Lyrics only, no lower thirds.' },
    },
    {
      type: 'song',
      title: SONG_TITLES[songs[1]],
      durationSeconds: 330,
      songId: songs[1],
      arrangementId: DEFAULT_ARRANGEMENT[songs[1]],
      techNotes: { lighting: 'Build on the bridge, blackout on the last hit.' },
    },
    {
      type: 'prayer',
      title: 'Prayer & Family News',
      durationSeconds: 300,
      responsiblePersonId: 'person_maria',
      techNotes: { video: 'Announcement slides 1-3.' },
    },
    { type: 'header', title: 'Word', durationSeconds: 0 },
    {
      type: 'sermon',
      title: sermonTitle,
      durationSeconds: 2100,
      responsiblePersonId: 'person_ian',
      description: `Part ${(weekIndex % 3) + 1} of the ${SERIES[weekIndex % SERIES.length].title} series.`,
      techNotes: {
        audio: 'Lav mic — check battery at the 20 minute mark.',
        video: 'Sermon slides from ProPresenter, camera 2 wide on transitions.',
        lighting: 'Preach look, house at 40%.',
        stage: 'Stool and side table centre stage.',
      },
    },
    { type: 'header', title: 'Response', durationSeconds: 0 },
    {
      type: 'song',
      title: SONG_TITLES[songs[2]],
      durationSeconds: 380,
      songId: songs[2],
      arrangementId: DEFAULT_ARRANGEMENT[songs[2]],
      description: 'Open-ended — watch the worship leader for the tag.',
      techNotes: { audio: 'Ready for spontaneous tag, keep pads up.' },
    },
    {
      type: 'announcement',
      title: 'Next Steps & Giving',
      durationSeconds: 240,
      responsiblePersonId: 'person_maria',
      techNotes: { video: 'Giving QR slide.' },
    },
    {
      type: 'song',
      title: SONG_TITLES[songs[3]],
      durationSeconds: 90,
      songId: songs[3],
      arrangementId: DEFAULT_ARRANGEMENT[songs[3]],
    },
    {
      type: 'transition',
      title: 'Dismissal & Cafe Open',
      durationSeconds: 120,
      techNotes: { audio: 'Post-service playlist up as the last chord rings out.', lighting: 'House to 100%.' },
    },
  ]

  return raw.map((item, index) => ({
    id: `${planId}_item_${String(index + 1).padStart(2, '0')}`,
    planId,
    type: item.type,
    title: item.title,
    description: item.description,
    durationSeconds: item.durationSeconds,
    sortOrder: index + 1,
    songId: item.songId,
    arrangementId: item.arrangementId,
    keyOverride: item.keyOverride,
    responsiblePersonId: item.responsiblePersonId,
    techNotes: item.techNotes,
    createdAt: now,
    updatedAt: now,
  }))
}

/** Deterministic pseudo-random so the demo looks the same on every reseed. */
function pick<T>(items: T[], salt: number): T | undefined {
  if (items.length === 0) return undefined
  return items[salt % items.length]
}

function statusFor(weekIndex: number, slot: number): SchedulingStatus {
  const seed = weekIndex * 7 + slot * 3
  if (weekIndex === 0) return seed % 9 === 0 ? 'pending' : 'confirmed'
  if (weekIndex === 1) return seed % 5 === 0 ? 'declined' : seed % 3 === 0 ? 'pending' : 'confirmed'
  if (weekIndex >= 4) return seed % 4 === 0 ? 'unscheduled' : 'pending'
  return seed % 6 === 0 ? 'declined' : seed % 2 === 0 ? 'pending' : 'confirmed'
}

function buildAssignments(
  planId: string,
  teamIds: string[],
  weekIndex: number,
  memberships: TeamMembership[],
  now: string,
): SchedulingAssignment[] {
  const positions = buildPositions()
  const result: SchedulingAssignment[] = []
  let slot = 0

  for (const teamId of teamIds) {
    const teamPositions = positions.filter((p) => p.teamId === teamId)
    for (const position of teamPositions) {
      const eligible = memberships.filter(
        (m) => m.teamId === teamId && m.positionIds.includes(position.id),
      )
      for (let n = 0; n < position.defaultQuantity; n += 1) {
        slot += 1
        const status = statusFor(weekIndex, slot)
        const candidate = pick(eligible, weekIndex + n + position.sortOrder)
        const personId = status === 'unscheduled' || !candidate ? null : candidate.personId
        result.push({
          id: `${planId}_asg_${position.id}_${n + 1}`,
          planId,
          positionId: position.id,
          teamId,
          personId,
          status: personId ? status : 'unscheduled',
          respondedAt: status === 'confirmed' || status === 'declined' ? now : undefined,
          declineReason: status === 'declined' ? 'Away that weekend' : undefined,
          createdAt: now,
          updatedAt: now,
        })
      }
    }
  }
  return result
}

export function buildPlans(): PlanBundle {
  const now = nowISO()
  const sundays = upcomingSundays(6)
  const serviceTypes = buildServiceTypes()
  const memberships = buildMemberships()

  const plans: Plan[] = []
  const planItems: PlanItem[] = []
  const assignments: SchedulingAssignment[] = []

  sundays.forEach((date, weekIndex) => {
    const series = SERIES[weekIndex % SERIES.length]

    // 9:30 gathering — fully planned.
    const mainId = `plan_${date.replace(/-/g, '')}_0930`
    plans.push({
      id: mainId,
      serviceTypeId: 'stype_sunday_930',
      title: `Sunday ${date.slice(8)} — 9:30`,
      serviceDate: date,
      startTime: '09:30',
      status: weekIndex < 3 ? 'published' : 'draft',
      seriesTitle: series.title,
      seriesArtworkColor: series.color,
      notes: weekIndex === 0 ? 'Rehearsal 8:00am. Full run from the top at 8:45.' : undefined,
      createdAt: now,
      updatedAt: now,
    })
    planItems.push(...buildItems(mainId, weekIndex, now))
    assignments.push(
      ...buildAssignments(
        mainId,
        serviceTypes[0].teamIds,
        weekIndex,
        memberships,
        now,
      ),
    )

    // 11:30 gathering — same order of service, lighter crew.
    if (weekIndex < 4) {
      const secondId = `plan_${date.replace(/-/g, '')}_1130`
      plans.push({
        id: secondId,
        serviceTypeId: 'stype_sunday_1130',
        title: `Sunday ${date.slice(8)} — 11:30`,
        serviceDate: date,
        startTime: '11:30',
        status: weekIndex < 2 ? 'published' : 'draft',
        seriesTitle: series.title,
        seriesArtworkColor: series.color,
        createdAt: now,
        updatedAt: now,
      })
      planItems.push(...buildItems(secondId, weekIndex, now))
      assignments.push(
        ...buildAssignments(secondId, serviceTypes[1].teamIds, weekIndex + 2, memberships, now),
      )
    }

    // Midweek prayer — minimal plan, first two weeks only.
    if (weekIndex < 2) {
      const midweekDate = wednesdayAfter(date)
      const midId = `plan_${midweekDate.replace(/-/g, '')}_1900`
      plans.push({
        id: midId,
        serviceTypeId: 'stype_midweek',
        title: 'Midweek Prayer',
        serviceDate: midweekDate,
        startTime: '19:00',
        status: 'published',
        createdAt: now,
        updatedAt: now,
      })
      planItems.push(
        {
          id: `${midId}_item_01`,
          planId: midId,
          type: 'custom',
          title: 'Welcome & Scripture',
          durationSeconds: 300,
          sortOrder: 1,
          responsiblePersonId: 'person_maria',
          createdAt: now,
          updatedAt: now,
        },
        {
          id: `${midId}_item_02`,
          planId: midId,
          type: 'song',
          title: SONG_TITLES.song_river_of_mercy,
          durationSeconds: 380,
          sortOrder: 2,
          songId: 'song_river_of_mercy',
          arrangementId: 'arr_river_of_mercy_default',
          createdAt: now,
          updatedAt: now,
        },
        {
          id: `${midId}_item_03`,
          planId: midId,
          type: 'prayer',
          title: 'Open Prayer',
          durationSeconds: 1800,
          sortOrder: 3,
          techNotes: { audio: 'Two handhelds in the room, keep pads soft.' },
          createdAt: now,
          updatedAt: now,
        },
      )
      assignments.push(
        ...buildAssignments(midId, serviceTypes[2].teamIds, weekIndex + 1, memberships, now),
      )
    }
  })

  // The seeded "You have been scheduled" notification points at this exact
  // assignment, so it must start out awaiting a response — otherwise the
  // inline Accept button in the feed would resolve something already confirmed.
  const pendingRequest = assignments.find(
    (a) => a.id === `plan_${sundays[2].replace(/-/g, '')}_0930_asg_pos_leader_1`,
  )
  if (pendingRequest) {
    pendingRequest.status = 'pending'
    pendingRequest.personId = CURRENT_PERSON_ID
    pendingRequest.respondedAt = undefined
  }

  return { plans, planItems, assignments }
}

export function buildBlockouts(): Blockout[] {
  const now = nowISO()
  const sundays = upcomingSundays(6)
  const b = (
    id: string,
    personId: string,
    startDate: string,
    endDate: string,
    reason: string,
    repeatsAnnually = false,
  ): Blockout => ({ id, personId, startDate, endDate, reason, repeatsAnnually, createdAt: now, updatedAt: now })

  return [
    b('blockout_01', 'person_tom', sundays[1], sundays[2], 'Family holiday'),
    b('blockout_02', 'person_grace', sundays[2], sundays[2], 'Out of town for a wedding'),
    b('blockout_03', 'person_kevin', sundays[3], sundays[4], 'Work travel'),
    b('blockout_04', 'person_ana', sundays[0], sundays[0], 'Unwell'),
    b('blockout_05', 'person_luke', sundays[4], sundays[5], 'University exams'),
    b('blockout_06', 'person_ruth', '2026-12-24', '2026-12-27', 'Christmas with family', true),
  ]
}

export function buildNotifications(): AppNotification[] {
  const now = Date.now()
  const minutesAgo = (m: number) => new Date(now - m * 60_000).toISOString()
  const sundays = upcomingSundays(6)
  const firstPlanId = `plan_${sundays[0].replace(/-/g, '')}_0930`
  const thirdPlanId = `plan_${sundays[2].replace(/-/g, '')}_0930`

  return [
    {
      id: 'notif_01',
      type: 'scheduling_request',
      title: 'You have been scheduled',
      body: `Worship Leader — Sunday ${sundays[2].slice(8)} at 9:30. Can you serve?`,
      priority: 'high',
      personId: CURRENT_PERSON_ID,
      isRead: false,
      actionResolved: false,
      link: `/plans/${thirdPlanId}`,
      relatedPlanId: thirdPlanId,
      relatedAssignmentId: `${thirdPlanId}_asg_pos_leader_1`,
      createdAt: minutesAgo(18),
      updatedAt: minutesAgo(18),
    },
    {
      id: 'notif_02',
      type: 'plan_update',
      title: 'Order of service updated',
      body: 'Maria moved "Prayer & Family News" before the sermon and trimmed the countdown to 8 minutes.',
      priority: 'normal',
      personId: null,
      isRead: false,
      actionResolved: true,
      link: `/plans/${firstPlanId}`,
      relatedPlanId: firstPlanId,
      createdAt: minutesAgo(95),
      updatedAt: minutesAgo(95),
    },
    {
      id: 'notif_03',
      type: 'blockout_conflict',
      title: 'Scheduling conflict',
      body: 'Tom Whitfield is blocked out on the date he is pencilled in for Bass Guitar.',
      priority: 'high',
      personId: CURRENT_PERSON_ID,
      isRead: false,
      actionResolved: true,
      link: '/schedule',
      createdAt: minutesAgo(240),
      updatedAt: minutesAgo(240),
    },
    {
      id: 'notif_04',
      type: 'song_added',
      title: 'New song in the library',
      body: '"River of Mercy" was added and exposed to the public church app.',
      priority: 'low',
      personId: null,
      isRead: true,
      actionResolved: true,
      link: '/songs/song_river_of_mercy',
      relatedSongId: 'song_river_of_mercy',
      createdAt: minutesAgo(1400),
      updatedAt: minutesAgo(1400),
    },
    {
      id: 'notif_05',
      type: 'general_alert',
      title: 'Rehearsal time change',
      body: 'This week only, band rehearsal starts at 8:00am instead of 8:15am.',
      priority: 'normal',
      personId: null,
      isRead: true,
      actionResolved: true,
      createdAt: minutesAgo(2880),
      updatedAt: minutesAgo(2880),
    },
    {
      id: 'notif_06',
      type: 'scheduling_request',
      title: 'Serving request accepted',
      body: 'You accepted Acoustic Guitar for the midweek prayer gathering.',
      priority: 'low',
      personId: CURRENT_PERSON_ID,
      isRead: true,
      actionResolved: true,
      actionTaken: 'accepted',
      createdAt: minutesAgo(4320),
      updatedAt: minutesAgo(4320),
    },
  ]
}

export function buildApiKeys(): ApiKey[] {
  const now = nowISO()
  return [
    {
      id: 'key_main_app',
      label: 'MyMDV main church app',
      key: 'mdv_live_7f3a91c4e28b5d60a1f9',
      scopes: ['songs:read', 'arrangements:read'],
      lastUsedAt: new Date(Date.now() - 36 * 60_000).toISOString(),
      revoked: false,
      createdAt: now,
      updatedAt: now,
    },
    {
      id: 'key_legacy_site',
      label: 'Legacy website widget (retired)',
      key: 'mdv_live_0c22ab74ff1e39d8b407',
      scopes: ['songs:read'],
      revoked: true,
      createdAt: now,
      updatedAt: now,
    },
  ]
}

export function buildSettings(): AppSettings {
  return {
    id: 'app-settings',
    organizationName: 'MDV Miami',
    currentPersonId: CURRENT_PERSON_ID,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    theme: 'light',
    publicApiEnabled: true,
    publicApiRequiresKey: false,
    weekStartsOn: 0,
    updatedAt: nowISO(),
  }
}
